import React, {memo} from 'react';
import ReactApexChart from 'react-apexcharts';

const parseHexColor = (value) => {
	if (typeof value !== 'string') {
		return null;
	}

	const normalized = value.trim();

	if (!/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(normalized)) {
		return null;
	}

	let hex = normalized.slice(1);

	if (hex.length === 3) {
		hex = hex.split('').map((char) => char + char).join('');
	}

	return {
		r: parseInt(hex.slice(0, 2), 16),
		g: parseInt(hex.slice(2, 4), 16),
		b: parseInt(hex.slice(4, 6), 16),
	};
};

const toRgba = (color, alpha) => {
	const parsedColor = parseHexColor(color);

	if (!parsedColor) {
		return color;
	}

	return `rgba(${parsedColor.r}, ${parsedColor.g}, ${parsedColor.b}, ${alpha})`;
};

function ChartArea({data, labels, color, linesColor = '#bbbbbb', variant = 'default', className = '', height = '100%'}) {
	const isGlassVariant = variant === 'glass';
	const chartLabelColor = isGlassVariant ? toRgba(linesColor, 0.9) : linesColor;
	const chartGridColor = isGlassVariant ? toRgba(linesColor, 0.24) : linesColor;
	const chartFillStart = isGlassVariant ? toRgba(color, 0.42) : color;
	const chartFillEnd = isGlassVariant ? toRgba(color, 0.02) : toRgba(color, 0.06);
	const chartStrokeColor = isGlassVariant ? toRgba(color, 0.98) : color;
	const chartGlowColor = isGlassVariant ? toRgba(color, 0.34) : toRgba(color, 0.18);

	var cs = {
		name: 'cs',
		options: {
			months: ['Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen', 'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'],
			shortMonths: ['Led', 'Úno', 'Bře', 'Dub', 'Kvě', 'Čvn', 'Čvc', 'Srp', 'Zář', 'Říj', 'Lis', 'Pro'],
			days: ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota'],
			shortDays: ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'],
			toolbar: {
				exportToSVG: 'Stáhnout SVG',
				exportToPNG: 'Stáhnout PNG',
				exportToCSV: 'Stáhnout CSV',
				menu: 'Menu',
				selection: 'Vybrat',
				selectionZoom: 'Zoom: Vybrat',
				zoomIn: 'Zoom: Přiblížit',
				zoomOut: 'Zoom: Oddálit',
				pan: 'Přesouvat',
				reset: 'Resetovat',
			},
		},
	};

	let gr = {
		series: [{name: 'Hodnota', data: data}],
		options: {
			chart: {
				width: '100%',
				height: '100%',
				offsetY: isGlassVariant ? -2 : -15,
				offsetX: isGlassVariant ? 0 : -5,
				parentHeightOffset: 0,
				background: 'transparent',
				locales: [cs],
				defaultLocale: 'cs',
				type: 'area',
				zoom: {enabled: false},
				stacked: false,
				toolbar: {show: false},
				dropShadow: isGlassVariant
					? {
						enabled: true,
						top: 10,
						left: 0,
						blur: 16,
						color: chartGlowColor,
						opacity: 0.24,
					}
					: {enabled: false},
			},
			colors: [color],
			dataLabels: {enabled: false},
			tooltip: {
				theme: 'dark',
				fillSeriesColor: false,
				style: {fontSize: isGlassVariant ? '1.1rem' : '1rem'},
				x: {show: true},
			},
			stroke: {curve: 'smooth', lineCap: 'round', width: isGlassVariant ? 2 : 2, colors: [chartStrokeColor]},
			fill: {
				type: 'gradient',
				gradient: {
					shadeIntensity: 1,
					type: 'vertical',
					opacityFrom: isGlassVariant ? 0.38 : 0.28,
					opacityTo: isGlassVariant ? 0.02 : 0.08,
					stops: [0, 72, 100],
					colorStops: [
						[{offset: 0, color: chartFillStart, opacity: isGlassVariant ? 0.42 : 0.28}],
						[{offset: 100, color: chartFillEnd, opacity: isGlassVariant ? 0.02 : 0.08}],
					],
				},
			},
			markers: {
				size: isGlassVariant ? 0 : 0,
				hover: {
					size: isGlassVariant ? 5 : 4,
					sizeOffset: 2,
				},
				strokeColors: '#f4fffa',
				strokeWidth: 2,
			},
			title: {text: '', align: 'left'},
			subtitle: {text: '', align: 'left'},
			labels: labels,
			legend: {horizontalAlign: 'left'},
			xaxis: {
				axisBorder: {show: false},
				labels: {
					formatter: function (value) {
						let date = new Date(value);
						return date.getDate() + '.' + (date.getMonth() + 1) + '.' + date.getFullYear();
					},
					show: false,
					rotate: 0,
					style: {fontSize: isGlassVariant ? '1.05rem' : '1rem', colors: [chartLabelColor]},
				},
				axisTicks: {
					show: false,
					tickAmount: 6,
				},
			},
			yaxis: {
				labels: {
					show: true,
					rotate: 0,
					style: {fontSize: isGlassVariant ? '1.05rem' : '1rem', colors: [chartLabelColor]},
				}
			},
			grid: {
				show: true,

				position: 'back',
				borderColor: chartGridColor,
				strokeDashArray: isGlassVariant ? 6 : 0,
				padding: isGlassVariant ? {left: 6, right: 12, top: 8, bottom: 0} : undefined,
				xaxis: {lines: {show: false}},
				yaxis: {lines: {show: true}},
			},
			noData: {
				text: 'Zatím bez dat',
				align: 'center',
				verticalAlign: 'middle',
				style: {
					color: chartLabelColor,
					fontSize: isGlassVariant ? '1.25rem' : '1.1rem',
				},
			},
		},
	};

	return (
		<div className={className}>
			<ReactApexChart options={gr.options} series={gr.series} type="area" width="100%" height={height} />
		</div>
	);
}

export default memo(ChartArea);
